import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MediaCard from "../components/MediaCard";
import FeedFYP from "../components/FeedFYP";
import SearchBox from "../components/SearchBox";
import { Loader2, Flame, Image as ImageIcon, Video, Clock, TrendingUp, Globe, Users, Hash, Sparkles, LayoutGrid, ChevronDown, BadgeCheck } from "lucide-react";

const PAGE = 24;
// Remembers the grid + scroll position per filter-set so returning from a
// media page restores instantly instead of refetching and losing your place.
const SCROLL_CACHE = {};

export default function Explore() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const q = params.get("q") || "";
  const tag = params.get("tag") || "";
  const [feed, setFeed] = useState(params.get("feed") === "following" ? "following" : "all"); // all | following
  const [viewMode, setViewMode] = useState(params.get("view") === "fyp" ? "fyp" : "grid"); // grid | fyp
  const [mediaType, setMediaType] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(params.get("verified") === "1");
  useEffect(() => {
    if ((params.get("verified") === "1") === verifiedOnly) return;
    const next = new URLSearchParams(params);
    if (verifiedOnly) next.set("verified", "1"); else next.delete("verified");
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifiedOnly]);
  const [sort, setSort] = useState(() => sessionStorage.getItem("ex_sort") || "recent");
  const [sortInit, setSortInit] = useState(false);
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [showTags, setShowTags] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const sentinel = useRef(null);

  const buildParams = useCallback((skip) => ({
    q, tag, media_type: mediaType, sort, skip, limit: PAGE,
    following: feed === "following",
    ...(verifiedOnly ? { verified: true } : {}),
  }), [q, tag, mediaType, sort, feed, verifiedOnly]);

  const load = useCallback(async () => {
    setLoading(true);
    setHasMore(true);
    try {
      const { data } = await api.get("/media", { params: buildParams(0) });
      setItems(data);
      setHasMore(data.length === PAGE);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const cacheKey = JSON.stringify(buildParams(0));

  // Restore a cached grid + scroll on mount (e.g. returning from a media page);
  // otherwise fetch. Filter changes produce a new cacheKey and fetch fresh.
  useEffect(() => {
    const cached = SCROLL_CACHE[cacheKey];
    if (cached && cached.items?.length) {
      setItems(cached.items);
      setHasMore(cached.hasMore);
      setLoading(false);
      requestAnimationFrame(() => requestAnimationFrame(() => window.scrollTo(0, cached.scrollY || 0)));
    } else {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // keep the cache fresh as the grid grows / user scrolls
  useEffect(() => {
    if (items.length) SCROLL_CACHE[cacheKey] = { ...(SCROLL_CACHE[cacheKey] || {}), items, hasMore };
  }, [items, hasMore, cacheKey]);
  useEffect(() => {
    const onScroll = () => { SCROLL_CACHE[cacheKey] = { ...(SCROLL_CACHE[cacheKey] || {}), scrollY: window.scrollY }; };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [cacheKey]);

  useEffect(() => {
    if (user && !sortInit) { if (!sessionStorage.getItem("ex_sort")) setSort("foryou"); setSortInit(true); }
  }, [user, sortInit]);
  useEffect(() => { sessionStorage.setItem("ex_sort", sort); }, [sort]);
  useEffect(() => {
    api.get("/tags/popular", { params: { limit: 24 } }).then(({ data }) => setTags(data)).catch(() => {});
  }, []);

  const loadMore = useCallback(async () => {
    if (loadingMore || loading || !hasMore) return;
    setLoadingMore(true);
    try {
      const { data } = await api.get("/media", { params: buildParams(items.length) });
      setItems((prev) => {
        const seen = new Set(prev.map((p) => p.id));
        return [...prev, ...data.filter((d) => !seen.has(d.id))];
      });
      setHasMore(data.length === PAGE);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, loading, hasMore, buildParams, items.length]);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "600px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const heading = q ? `Search · ${q}` : tag ? `#${tag}` : feed === "following" ? "Following" : "Explore";
  const showHero = !q && !tag && feed === "all";
  const searching = !!(q || tag);
  const showFYP = viewMode === "fyp" && !searching;

  if (showFYP) {
    return (
      <div className="bg-black">
        <div className="sticky top-0 z-20 flex items-center justify-center gap-1.5 sm:gap-2 py-2 px-16 sm:px-6 border-b border-[#1C1C1F] bg-black/95 backdrop-blur flex-wrap">
          <Filter active onClick={() => {}} testid="view-fyp"><Sparkles size={13} /> For You</Filter>
          <Filter active={false} onClick={() => setViewMode("grid")} testid="view-grid"><LayoutGrid size={13} /> Grid</Filter>
          {user && (
            <>
              <span className="w-px h-5 bg-[#1C1C1F] mx-1" />
              <Filter active={feed === "all"} onClick={() => setFeed("all")} testid="fyp-tab-all"><Globe size={13} /> All</Filter>
              <Filter active={feed === "following"} onClick={() => setFeed("following")} testid="fyp-tab-following"><Users size={13} /> Following</Filter>
            </>
          )}
          <span className="w-px h-5 bg-[#1C1C1F] mx-1" />
          <Filter active={mediaType === ""} onClick={() => setMediaType("")} testid="fyp-filter-all">All</Filter>
          <Filter active={mediaType === "video"} onClick={() => setMediaType("video")} testid="fyp-filter-videos"><Video size={13} /> Videos</Filter>
          <Filter active={mediaType === "image"} onClick={() => setMediaType("image")} testid="fyp-filter-images"><ImageIcon size={13} /> Photos</Filter>
        </div>
        <FeedFYP key={`${feed}-${mediaType}`} mediaType={mediaType} following={feed === "following"} />
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {showHero && (
        <div className="relative overflow-hidden border-b border-white/[0.07]">
          <div className="absolute inset-0 opacity-[0.16]" style={{ backgroundImage: "radial-gradient(60% 80% at 75% 0%, #0066FF, transparent 60%)" }} />
          <div className="absolute inset-0 opacity-[0.10]" style={{ backgroundImage: "radial-gradient(50% 60% at 10% 100%, #00E5FF, transparent 55%)" }} />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#020202] via-[#020202]/70 to-transparent" />
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16 sm:pt-28 sm:pb-24 relative">
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.04] backdrop-blur px-3.5 py-1.5 mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] shadow-[0_0_10px_#00E5FF] animate-pulse" />
              <span className="font-mono text-[10px] tracking-[0.25em] text-white/70 uppercase">Uncensored · Indexed · 18+</span>
            </div>
            <h1 className="font-heading text-6xl sm:text-8xl lg:text-[8.5rem] uppercase tracking-[-0.04em] leading-[0.82] text-white">
              The wall<br />of{" "}
              <span className="bg-gradient-to-r from-[#00E5FF] via-[#3b9cff] to-[#0066FF] bg-clip-text text-transparent">kink</span>
            </h1>
            <p className="text-[#9aa0aa] mt-7 max-w-xl text-base sm:text-lg leading-relaxed">
              Every photo, set and film — preserved, understood and instantly searchable. A living archive of queer erotic history. <span className="text-white/85">Preservation, not just porn.</span>
            </p>
            <div className="mt-9 max-w-2xl">
              <SearchBox variant="hero" />
            </div>
          </div>
        </div>
      )}

      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        {user && !q && !tag && (
          <div className="flex items-center gap-2 mb-6">
            <Tab active={feed === "all"} onClick={() => setFeed("all")} testid="feed-tab-all"><Globe size={14} /> Explore</Tab>
            <Tab active={feed === "following"} onClick={() => setFeed("following")} testid="feed-tab-following"><Users size={14} /> Following</Tab>
          </div>
        )}

        <div className="mb-5">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] tracking-[0.3em] text-[#00E5FF] uppercase">The Collection</span>
            <span className="h-px flex-1 bg-gradient-to-r from-white/15 to-transparent" />
          </div>
        </div>

        <div className="sticky top-16 z-30 flex flex-wrap items-center justify-between gap-4 mb-8 rounded-2xl border border-white/[0.07] bg-[#0A0A0C]/80 backdrop-blur-xl px-5 py-3.5 shadow-[0_12px_34px_-14px_rgba(0,0,0,0.9)]">
          <div className="flex items-center gap-3">
            <h2 data-testid="feed-heading" className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-white">{heading}</h2>
            {(q || tag) && (
              <button data-testid="clear-search-btn" onClick={() => setParams({})} className="font-mono text-[10px] uppercase tracking-wider text-[#A1A1AA] hover:text-[#00E5FF] border border-white/10 hover:border-[#00E5FF] px-3 py-1.5 rounded-full transition-colors">
                Clear ✕
              </button>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {!searching && <Filter active={false} onClick={() => setViewMode("fyp")} testid="view-fyp-from-grid"><Sparkles size={13} /> FYP Feed</Filter>}
            <Filter active={verifiedOnly} onClick={() => setVerifiedOnly((v) => !v)} testid="filter-verified"><BadgeCheck size={14} /> Verified archivists only</Filter>
            <FilterSelect
              label="Type"
              value={mediaType}
              onChange={setMediaType}
              options={[
                { id: "", label: "All media", icon: LayoutGrid, testid: "filter-all" },
                { id: "image", label: "Photos", icon: ImageIcon, testid: "filter-images" },
                { id: "video", label: "Videos", icon: Video, testid: "filter-videos" },
              ]}
            />
            <FilterSelect
              label="Sort"
              value={sort}
              onChange={setSort}
              options={[
                ...(user ? [{ id: "foryou", label: "For You", icon: Sparkles, testid: "sort-foryou" }] : []),
                { id: "recent", label: "Newest", icon: Clock, testid: "sort-recent" },
                { id: "popular", label: "Hottest", icon: TrendingUp, testid: "sort-popular" },
              ]}
            />
          </div>
        </div>

        {tags.length > 0 && feed === "all" && (
          <div className="mb-8">
            <button
              data-testid="toggle-tags-btn"
              onClick={() => setShowTags((s) => !s)}
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-[#A1A1AA] hover:text-[#0066FF] transition-colors mb-3"
            >
              <Hash size={12} /> {showTags ? "Hide tags" : "Browse tags"}
            </button>
            {showTags && (
              <div className="flex flex-wrap gap-2 fade-up">
                {tags.map((t) => (
                  <button
                    key={t.tag}
                    data-testid={`tag-pill-${t.tag}`}
                    onClick={() => setParams({ tag: t.tag })}
                    className={`inline-flex items-center gap-1.5 border px-3 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                      tag === t.tag ? "border-[#0066FF] text-[#0066FF] bg-[#0A2540]" : "border-[#1C1C1F] bg-[#141414] text-[#A1A1AA] hover:text-white hover:border-[#2A2A2E]"
                    }`}
                  >
                    #{t.tag} <span className="text-[#52525B]">{t.count}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-32"><Loader2 className="animate-spin text-[#0066FF]" size={32} /></div>
        ) : items.length === 0 ? (
          <div className="text-center py-32">
            {verifiedOnly ? (
              <div data-testid="verified-empty">
                <p className="font-heading text-3xl uppercase text-[#2A2A2E] mb-3">No verified archives here yet</p>
                <p className="text-[#52525B] text-sm mb-6">Nothing from verified archivists matches these filters.</p>
                <button data-testid="verified-empty-show-all" onClick={() => setVerifiedOnly(false)} className="inline-block bg-[#0066FF] text-white font-heading uppercase tracking-widest text-sm px-8 py-3 rounded-full hover:bg-[#0055CC] transition-colors">Show everyone</button>
              </div>
            ) : feed === "following" ? (
              <>
                <p className="font-heading text-3xl uppercase text-[#2A2A2E] mb-3">Your feed is empty</p>
                <p className="text-[#52525B] text-sm mb-6">Follow some creators to see their drops here.</p>
                <button onClick={() => setFeed("all")} className="inline-block bg-[#0066FF] text-white font-heading uppercase tracking-widest text-sm px-8 py-3 rounded-full hover:bg-[#0055CC] hover:shadow-[0_8px_30px_rgba(0,102,255,0.4)] transition-all">Explore creators</button>
              </>
            ) : (
              <>
                <p className="font-heading text-3xl uppercase text-[#2A2A2E] mb-3">Nothing archived yet</p>
                <p className="text-[#52525B] text-sm mb-6">Be the first to preserve something.</p>
                <Link to="/upload" className="inline-block bg-[#0066FF] text-white font-heading uppercase tracking-widest text-sm px-8 py-3 rounded-full hover:bg-[#0055CC] hover:shadow-[0_8px_30px_rgba(0,102,255,0.4)] transition-all">Upload Media</Link>
              </>
            )}
          </div>
        ) : (
          <>
            <div className="columns-2 md:columns-3 lg:columns-4 gap-3 sm:gap-5" data-testid="media-grid">
              {items.map((item, i) => <MediaCard key={item.id} item={item} index={i} />)}
            </div>
            <div ref={sentinel} className="h-16 flex items-center justify-center mt-4" data-testid="infinite-sentinel">
              {loadingMore && <Loader2 className="animate-spin text-[#0066FF]" size={24} />}
              {!hasMore && (
                <span className="flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-[#3a3a3e]">
                  <span className="h-px w-8 bg-[#D4AF37]/40" /> end of the archive <span className="h-px w-8 bg-[#D4AF37]/40" />
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Filter({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      aria-pressed={active}
      className={`flex items-center gap-1.5 px-4 py-2 rounded-full font-heading uppercase tracking-widest text-xs transition-all border ${
        active ? "bg-white text-black border-white shadow-[0_4px_16px_rgba(255,255,255,0.15)]" : "bg-white/[0.03] text-[#A1A1AA] border-white/10 hover:text-white hover:border-white/25 hover:-translate-y-0.5"
      }`}
    >
      {children}
    </button>
  );
}

function FilterSelect({ label, value, onChange, options }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = options.find((o) => o.id === value) || options[0];
  const CurIcon = current?.icon;
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={`filter-select-${label.toLowerCase()}`}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-full bg-white/[0.04] border border-white/[0.08] px-4 py-2 font-heading uppercase tracking-widest text-xs text-white hover:border-[#0066FF] transition-colors"
        style={open ? { borderColor: "#0066FF" } : undefined}
      >
        <span className="text-[#52525B]">{label}:</span>
        {CurIcon && <CurIcon size={13} className="text-[#0066FF]" />}
        <span>{current?.label}</span>
        <ChevronDown size={14} className={`text-[#52525B] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-40 min-w-[170px] bg-[#0A0A0A] border border-white/10 rounded-xl shadow-[0_20px_50px_rgba(0,0,0,0.7)] overflow-hidden py-1 animate-in fade-in slide-in-from-top-1 duration-150">
          {options.map((o) => {
            const Icon = o.icon;
            const active = o.id === value;
            return (
              <button
                key={o.testid}
                type="button"
                data-testid={o.testid}
                onClick={() => { onChange(o.id); setOpen(false); }}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left font-heading uppercase tracking-widest text-[11px] transition-colors ${
                  active ? "text-white bg-white/[0.06]" : "text-[#A1A1AA] hover:text-white hover:bg-white/[0.04]"
                }`}
              >
                {Icon && <Icon size={13} style={{ color: active ? "#0066FF" : "#52525B" }} />} {o.label}
                {active && <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#0066FF] shadow-[0_0_8px_#0066FF]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Tab({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex items-center gap-2 px-5 py-2.5 font-heading uppercase tracking-widest text-sm transition-colors border-b-2 ${
        active ? "text-white border-[#0066FF]" : "text-[#52525B] border-transparent hover:text-[#A1A1AA]"
      }`}
    >
      {children}
    </button>
  );
}
